import { jwtDecode } from 'jwt-decode';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { logout, selectAuthLoading, selectUser, setUser } from '../redux/auth/authSlice';
import { refreshAccessToken } from '../redux/auth/authUtils';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { persistor, store } from '../redux/store';

const PUBLIC_ROUTES = [
  '/login',
  '/register',
  '/forget-password',
  '/reset-password',
  '/privacy-policy',
  '/pricing',
];

const AUTH_BOUNCE_ROUTES = ['/login', '/register', '/forget-password', '/reset-password'];

const isPublicRoute = (url: string) => {
  return PUBLIC_ROUTES.some((route) => url.startsWith(route));
};

const isAuthBounceRoute = (url: string) => {
  return AUTH_BOUNCE_ROUTES.some((route) => url.startsWith(route));
};

const useCheckUserData = () => {
  const user = useAppSelector(selectUser);
  const authLoading = useAppSelector(selectAuthLoading);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const url = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const hasResetToken = url === '/reset-password' && searchParams.has('token');
  const shouldLogout = searchParams.get('logout') === 'true';

  const checkUserData = useCallback(async () => {
    if (shouldLogout) {
      persistor.purge();
      dispatch(logout());
      return;
    }

    // Special case: If we're on reset-password with a token, log out current user
    if (hasResetToken && user) {
      persistor.purge();
      dispatch(logout());
      return;
    }

    if (!authLoading && !user && !accessToken && !isPublicRoute(url)) {
      navigate('/login');
    } else if (accessToken && !user) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const decodedToken = jwtDecode<any>(accessToken);
      if (decodedToken.exp * 1000 < Date.now()) {
        // refresh token
        await refreshAccessToken(store.dispatch, store.getState);
      }

      dispatch(setUser(decodedToken.user));

      // am i in the right place?
      if (isAuthBounceRoute(url) && !hasResetToken) {
        navigate('/');
      }
    } else if (user) {
      // am i in the right place?
      if (isAuthBounceRoute(url) && !hasResetToken) {
        navigate('/');
      }
    }
  }, [shouldLogout, hasResetToken, user, authLoading, accessToken, url, dispatch, navigate]);

  useEffect(() => {
    checkUserData();
  }, [checkUserData]);
};

export default useCheckUserData;
